import { CheckCircle2 } from "lucide-react";
import MissingSkillsList from "./MissingSkillsList";

const RECO_THEME = {
  Primary: {
    eyebrow: "text-emerald-700",
    pulse: "bg-emerald-600",
    pill: "bg-emerald-700 text-white",
    border: "border-emerald-200 primary-role-glow",
    verdictBg: "bg-emerald-50 border-emerald-200 text-emerald-900",
    verdictTitle: "text-emerald-800",
    verdictHeadline: "Suitable to apply now.",
  },
  Secondary: {
    eyebrow: "text-slate-700",
    pulse: "bg-slate-600",
    pill: "bg-slate-800 text-white",
    border: "border-slate-200",
    verdictBg: "bg-slate-50 border-slate-200 text-slate-900",
    verdictTitle: "text-slate-800",
    verdictHeadline: "Solid backup choice.",
  },
  Stretch: {
    eyebrow: "text-amber-700",
    pulse: "bg-amber-600",
    pill: "bg-amber-600 text-white",
    border: "border-amber-200",
    verdictBg: "bg-amber-50 border-amber-200 text-amber-900",
    verdictTitle: "text-amber-800",
    verdictHeadline: "Stretch role — realistic next step.",
  },
  "Closest contender": {
    eyebrow: "text-slate-500",
    pulse: "bg-slate-400",
    pill: "bg-slate-500 text-white",
    border: "border-slate-200",
    verdictBg: "bg-slate-50 border-slate-200 text-slate-700",
    verdictTitle: "text-slate-700",
    verdictHeadline: "Not ready yet — close the critical gaps first.",
  },
};

const Bar = ({ label, value }) => {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  return (
    <div className="flex items-center gap-3 text-[11px]">
      <span className="w-20 sm:w-24 uppercase tracking-wider font-semibold text-slate-500 shrink-0">
        {label}
      </span>
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-slate-900 rounded-full transition-all"
          style={{ width: `${v}%` }}
        />
      </div>
      <span className="w-8 text-right font-mono font-bold text-slate-900 text-xs">
        {v}
      </span>
    </div>
  );
};

const _eyebrowLabel = (role, isBestFit) => {
  if (isBestFit) return "Best fit — start here";
  if (role.recommendation === "Primary") return "Your result · Primary fit";
  return `${role.recommendation} fit`;
};

const _verdictExplainer = (role) => {
  const mandatoryGap = role.missing_mandatory?.length || 0;
  const strongScore = role.final_score >= 70 && mandatoryGap > 0;
  const underpromoted =
    role.recommendation === "Stretch" ||
    role.recommendation === "Closest contender";
  if (!(strongScore && underpromoted)) return null;
  const sample = role.missing_mandatory.slice(0, 2).join(", ");
  const ellipsis = mandatoryGap > 2 ? "…" : "";
  const plural = mandatoryGap === 1 ? "" : "s";
  return `Your score is ${Math.round(role.final_score)} on raw fit, but ${mandatoryGap} mandatory skill${plural} (${sample}${ellipsis}) hold this back from a confident apply.`;
};

const _verdictBody = (role, c) => {
  const bits = [];
  const tech = c.technical_fit ?? 0;
  if (tech >= 80) bits.push("Strong technical match");
  else if (tech >= 60) bits.push("Decent technical match");
  else bits.push("Technical match needs work");

  const mGap = role.missing_mandatory?.length || 0;
  const iGap = role.missing_important?.length || 0;
  if (mGap === 0 && iGap === 0) {
    bits.push("no mandatory or important gaps");
  } else if (mGap === 0) {
    bits.push(`${iGap} important skill${iGap === 1 ? "" : "s"} to close`);
  } else if (mGap <= 3) {
    const sample = role.missing_mandatory.slice(0, 2).join(", ");
    const ellipsis = mGap > 2 ? "…" : "";
    bits.push(
      `${mGap} mandatory skill${mGap === 1 ? "" : "s"} to close (${sample}${ellipsis})`,
    );
  } else {
    bits.push(
      `${mGap} mandatory skills to close — start with ${role.missing_mandatory[0]}`,
    );
  }
  return bits.join(" · ");
};

const RoleResultHeader = ({ role, theme, isBestFit }) => (
  <>
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <span
        className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] ${theme.eyebrow}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${theme.pulse} animate-pulse`} />
        {_eyebrowLabel(role, isBestFit)}
      </span>
      <span
        data-testid={`reco-pill-${role.role_id}`}
        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${theme.pill}`}
      >
        {role.recommendation}
      </span>
    </div>
    <h3 className="mt-3 font-display text-2xl sm:text-3xl font-black text-slate-900 leading-tight">
      {role.role_name}
    </h3>
    <p className="text-xs text-slate-500 font-semibold mt-0.5">
      {role.role_category || "Role"} · Demand:{" "}
      {(role.components?.demand_level || "Moderate").replace(" (default)", "")}
    </p>
  </>
);

const RoleResultScore = ({ role, deltaText }) => (
  <div className="mt-4 flex items-baseline gap-2">
    <span
      data-testid={`final-score-${role.role_id}`}
      className="font-display text-5xl sm:text-6xl font-black text-slate-900 leading-none"
    >
      {Math.round(role.final_score)}
    </span>
    <span className="text-sm font-bold text-slate-400">/ 100</span>
    {deltaText && (
      <span className="ml-auto text-xs font-mono text-emerald-700 font-semibold">
        ↑ {deltaText}
      </span>
    )}
  </div>
);

const RoleResultVerdict = ({ role, theme }) => {
  const c = role.components || {};
  const explainer = _verdictExplainer(role);
  return (
    <div
      data-testid={`verdict-${role.role_id}`}
      className={`mt-5 rounded-md border px-3 py-2.5 text-[12px] leading-relaxed ${theme.verdictBg}`}
    >
      <span className={`font-semibold ${theme.verdictTitle}`}>
        {theme.verdictHeadline}
      </span>{" "}
      {_verdictBody(role, c)}
      {explainer && (
        <div
          data-testid={`verdict-explainer-${role.role_id}`}
          className="mt-2 text-[12px] text-slate-700 leading-relaxed"
        >
          {explainer}
        </div>
      )}
    </div>
  );
};

export const RoleResultCard = ({ role, deltaText, isBestFit = false }) => {
  if (!role) return null;
  const theme = RECO_THEME[role.recommendation] || RECO_THEME["Stretch"];
  const c = role.components || {};
  const hasNoGaps =
    (role.missing_mandatory?.length || 0) === 0 &&
    (role.missing_important?.length || 0) === 0;

  return (
    <div
      data-testid={`role-card-${role.role_id}`}
      className={`relative rounded-2xl border ${theme.border} bg-white p-6 lg:p-8 shadow-xl shadow-slate-900/5 transition-all hover:-translate-y-0.5`}
    >
      <RoleResultHeader role={role} theme={theme} isBestFit={isBestFit} />
      <RoleResultScore role={role} deltaText={deltaText} />

      {/* Component bars — Skills Match drivers only. */}
      <div className="mt-5 space-y-2.5">
        <Bar label="Technical" value={c.technical_fit} />
        <Bar label="Soft" value={c.soft_fit} />
        <Bar label="Demand" value={c.demand_score} />
      </div>

      <RoleResultVerdict role={role} theme={theme} />

      <MissingSkillsList
        skills={role.missing_mandatory}
        tone="rose"
        heading="Your quickest wins to unlock this role"
        total={role.mandatory_total}
        testId={`missing-mandatory-${role.role_id}`}
      />
      <MissingSkillsList
        skills={role.missing_important}
        tone="amber"
        heading="Skills that strengthen your application"
        maxShow={8}
        testId={`missing-important-${role.role_id}`}
      />

      {role.top_gaps?.length > 0 && (
        <div className="mt-3 text-sm text-slate-700">
          <span className="font-semibold text-slate-900">Top 3 gaps:</span>{" "}
          {role.top_gaps.join(", ")}
        </div>
      )}

      {hasNoGaps && (
        <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <CheckCircle2 size={16} />
          No mandatory or important skill gaps for this role.
        </div>
      )}
    </div>
  );
};

export default RoleResultCard;
